import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { scheduleAPI, carpoolsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import { PageLoading } from '../components/appPage';
import './Schedule.css';

const Schedule = () => {
  const { t } = useTranslation();
  const { carpoolId } = useParams();
  const { user } = useAuth();
  const [rides, setRides] = useState([]);
  const [carpool, setCarpool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  const [showDeleteRideModal, setShowDeleteRideModal] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [showAddRideModal, setShowAddRideModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [selectedRideId, setSelectedRideId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // New ride form state
  const [newRide, setNewRide] = useState({
    scheduledDate: '',
    scheduledTime: '',
    childId: '',
    driverId: ''
  });

  useEffect(() => {
    loadData();
  }, [carpoolId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [scheduleRes, carpoolRes] = await Promise.all([
        scheduleAPI.getSchedule(carpoolId),
        carpoolsAPI.getCarpool(carpoolId)
      ]);
      setRides(scheduleRes.data.rides || []);
      setCarpool(carpoolRes.data.carpool);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check if a ride is locked (within 2 hours of scheduled time)
  const isRideLocked = (ride) => {
    if (!ride.scheduledDate || !ride.scheduledTime) return false;

    const scheduledDateTime = new Date(ride.scheduledDate);
    const [hours, minutes] = ride.scheduledTime.split(':').map(Number);
    scheduledDateTime.setHours(hours, minutes, 0, 0);

    const now = new Date();
    const twoHoursBeforeRide = new Date(scheduledDateTime.getTime() - 2 * 60 * 60 * 1000);

    return now >= twoHoursBeforeRide;
  };

  // Group rides by date
  const groupedRides = useMemo(() => {
    const groups = {};
    rides.forEach(ride => {
      const dateKey = new Date(ride.scheduledDate).toISOString().split('T')[0];
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(ride);
    });

    // Sort dates
    const sortedKeys = Object.keys(groups).sort((a, b) => new Date(a) - new Date(b));
    const sortedGroups = {};
    sortedKeys.forEach(key => {
      sortedGroups[key] = groups[key].sort((a, b) =>
        a.scheduledTime.localeCompare(b.scheduledTime)
      );
    });

    return sortedGroups;
  }, [rides]);

  const handleGenerateSchedule = async () => {
    if (rides.length > 0) {
      setShowRegenerateModal(true);
      return;
    }
    await generateSchedule();
  };

  const generateSchedule = async () => {
    setGenerating(true);
    setShowRegenerateModal(false);

    try {
      const today = new Date();
      const nextMonth = new Date(today);
      nextMonth.setMonth(nextMonth.getMonth() + 1);

      await scheduleAPI.generateSchedule({
        carpoolId,
        startDate: today.toISOString(),
        endDate: nextMonth.toISOString()
      });

      await loadData();
      setSuccessMessage('Графикът е генериран успешно!');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Failed to generate schedule:', error);
      const msg = error.response?.data?.error?.message || t('errors.generic');
      setErrorMessage(msg);
      setShowErrorModal(true);
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteRideClick = (rideId) => {
    const ride = rides.find(r => r.id === rideId);
    if (ride && isRideLocked(ride)) {
      setErrorMessage('Това пътуване е заключено за промени (по-малко от 2 часа до тръгване)');
      setShowErrorModal(true);
      return;
    }
    setSelectedRideId(rideId);
    setShowDeleteRideModal(true);
  };

  const confirmDeleteRide = async () => {
    setShowDeleteRideModal(false);

    try {
      await scheduleAPI.deleteRide(selectedRideId);
      await loadData();
      setSuccessMessage('Пътуването е изтрито успешно');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Failed to delete ride:', error);
      const msg = error.response?.data?.error?.message || t('errors.generic');
      setErrorMessage(msg);
      setShowErrorModal(true);
    }
  };

  const confirmDeleteSchedule = async () => {
    setShowDeleteAllModal(false);

    try {
      await scheduleAPI.deleteSchedule(carpoolId);
      await loadData();
      setSuccessMessage('Графикът е изтрит успешно');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Failed to delete schedule:', error);
      setErrorMessage(t('errors.generic'));
      setShowErrorModal(true);
    }
  };

  const handleVolunteerDriver = async (rideId) => {
    const ride = rides.find(r => r.id === rideId);
    if (ride && isRideLocked(ride)) {
      setErrorMessage('Това пътуване е заключено за промени');
      setShowErrorModal(true);
      return;
    }

    try {
      const response = await scheduleAPI.requestDriver(rideId);
      setSuccessMessage(response.data?.message || 'Заявката е изпратена успешно!');
      setShowSuccessModal(true);
      await loadData();
    } catch (error) {
      console.error('Failed to volunteer:', error);
      setErrorMessage(error.response?.data?.error?.message || t('errors.generic'));
      setShowErrorModal(true);
    }
  };

  const handleApproveRequest = async (rideId, requestId) => {
    try {
      const response = await scheduleAPI.approveDriverRequest(rideId, requestId);
      setSuccessMessage(response.data?.message || 'Шофьорът е одобрен!');
      setShowSuccessModal(true);
      await loadData();
    } catch (error) {
      console.error('Failed to approve:', error);
      setErrorMessage(error.response?.data?.error?.message || t('errors.generic'));
      setShowErrorModal(true);
    }
  };

  const handleDeclineRequest = async (rideId, requestId) => {
    try {
      await scheduleAPI.declineDriverRequest(rideId, requestId);
      setSuccessMessage('Заявката е отхвърлена');
      setShowSuccessModal(true);
      await loadData();
    } catch (error) {
      console.error('Failed to decline:', error);
      setErrorMessage(error.response?.data?.error?.message || t('errors.generic'));
      setShowErrorModal(true);
    }
  };

  const handleUnassignDriver = async (rideId) => {
    const ride = rides.find(r => r.id === rideId);
    if (ride && isRideLocked(ride)) {
      setErrorMessage('Това пътуване е заключено за промени (по-малко от 2 часа до тръгване)');
      setShowErrorModal(true);
      return;
    }

    try {
      const response = await scheduleAPI.unassignDriver(rideId);
      setSuccessMessage(response.data?.message || 'Успешно се отписахте като шофьор');
      setShowSuccessModal(true);
      await loadData();
    } catch (error) {
      console.error('Failed to unassign:', error);
      setErrorMessage(error.response?.data?.error?.message || t('errors.generic'));
      setShowErrorModal(true);
    }
  };

  const handleAddRide = async (e) => {
    e.preventDefault();

    try {
      await scheduleAPI.createRide({
        carpoolId,
        ...newRide
      });
      setShowAddRideModal(false);
      setNewRide({ scheduledDate: '', scheduledTime: '', childId: '', driverId: '' });
      await loadData();
      setSuccessMessage('Пътуването е добавено успешно!');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Failed to add ride:', error);
      setErrorMessage(error.response?.data?.error?.message || t('errors.generic'));
      setShowErrorModal(true);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return { main: 'Днес', sub: date.toLocaleDateString('bg-BG', { day: 'numeric', month: 'long' }) };
    }
    if (date.toDateString() === tomorrow.toDateString()) {
      return { main: 'Утре', sub: date.toLocaleDateString('bg-BG', { day: 'numeric', month: 'long' }) };
    }

    return {
      main: date.toLocaleDateString('bg-BG', { weekday: 'long' }),
      sub: date.toLocaleDateString('bg-BG', { day: 'numeric', month: 'long' })
    };
  };

  if (loading) {
    return (
      <div className="schedule-page page-shell">
        <PageLoading message={t('schedule.loading', 'Зареждане на графика...')} />
      </div>
    );
  }

  return (
    <div className="schedule-page page-shell">
      {/* Header */}
      <div className="schedule-header">
        <div className="schedule-header-left">
          <Link to={`/carpools/${carpoolId}`} className="back-link">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Назад
          </Link>
          <h1 className="schedule-title">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            График на пътуванията
          </h1>
          {carpool && (
            <span className="carpool-name-badge">{carpool.name}</span>
          )}
        </div>

        <div className="schedule-header-right">
          <div className="view-toggle">
            <button
              className={`view-btn ${viewMode === 'calendar' ? 'active' : ''}`}
              onClick={() => setViewMode('calendar')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" />
                <line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
            </button>
          </div>

          <div className="schedule-actions">
            <button
              onClick={() => setShowAddRideModal(true)}
              className="btn-secondary"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Добави пътуване
            </button>

            <button
              onClick={handleGenerateSchedule}
              disabled={generating}
              className="btn-primary"
            >
              {generating ? (
                <>
                  <span className="spinner-small"></span>
                  Генериране...
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                    <polyline points="17 6 23 6 23 12" />
                  </svg>
                  {rides.length > 0 ? 'Регенерирай' : 'Генерирай график'}
                </>
              )}
            </button>

            {rides.length > 0 && (
              <button
                onClick={() => setShowDeleteAllModal(true)}
                className="btn-danger-outline"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
                Изтрий всички
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      {rides.length > 0 && (
        <div className="schedule-stats">
          <div className="stat-item">
            <span className="stat-value">{rides.length}</span>
            <span className="stat-label">Пътувания</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{Object.keys(groupedRides).length}</span>
            <span className="stat-label">Дни</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">
              {rides.filter(r => r.driver?.id).length}
            </span>
            <span className="stat-label">С шофьор</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">
              {rides.filter(r => !r.driver?.id).length}
            </span>
            <span className="stat-label">Нужен шофьор</span>
          </div>
        </div>
      )}

      {/* Content */}
      {rides.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </div>
          <h3>Няма генериран график</h3>
          <p>Генерирайте график, за да организирате пътуванията на карпула</p>
          <button onClick={handleGenerateSchedule} className="btn-primary btn-lg">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
              <polyline points="17 6 23 6 23 12" />
            </svg>
            Генерирай график
          </button>
        </div>
      ) : viewMode === 'calendar' ? (
        <div className="schedule-calendar">
          {Object.entries(groupedRides).map(([dateKey, dayRides]) => {
            const dateInfo = formatDate(dateKey);
            const isToday = new Date(dateKey).toDateString() === new Date().toDateString();
            const firstRide = dayRides[0];
            const dayDriver = firstRide?.driver;
            const dayTime = firstRide?.scheduledTime;
            const isLocked = firstRide && isRideLocked(firstRide);
            const isUserDriver = dayDriver?.id === user?.id;

            // Check if current user has pending request for this day
            const userHasPendingRequest = dayRides.some(ride =>
              ride.driverRequests?.some(req => req.userId === user?.id && req.status === 'pending')
            );

            // Get all pending requests for this day (from any ride)
            const pendingRequests = dayRides.flatMap(ride =>
              (ride.driverRequests || []).filter(req => req.status === 'pending')
            ).filter((req, index, self) =>
              self.findIndex(r => r.id === req.id) === index
            );

            return (
              <div key={dateKey} className={`day-card ${isToday ? 'today' : ''} ${isLocked ? 'locked' : ''}`}>
                {/* Day Header with Driver */}
                <div className="day-card-header">
                  <div className="day-card-date">
                    <span className="day-main">{dateInfo.main}</span>
                    <span className="day-sub">{dateInfo.sub}</span>
                  </div>

                  <div className="day-card-time">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    {dayTime}
                  </div>

                  {isLocked && (
                    <div className="locked-badge" title="Заключено - по-малко от 2ч до тръгване">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Driver Section */}
                <div className={`day-driver-section ${isUserDriver ? 'is-you' : ''}`}>
                  {dayDriver ? (
                    <div className="day-driver">
                      <div className="day-driver-avatar">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      </div>
                      <div className="day-driver-info">
                        <span className="day-driver-label">Шофьор</span>
                        <span className="day-driver-name">
                          {dayDriver.firstName} {dayDriver.lastName}
                          {isUserDriver && <span className="you-badge">Вие</span>}
                        </span>
                        {dayDriver.phone && (
                          <a href={`tel:${dayDriver.phone}`} className="day-driver-phone">{dayDriver.phone}</a>
                        )}
                      </div>

                      {/* Swap request button - only if not locked and not the current driver */}
                      {!isLocked && !isUserDriver && !userHasPendingRequest && (
                        <button
                          className="swap-request-btn"
                          onClick={() => handleVolunteerDriver(firstRide.id)}
                          title="Заяви се да шофираш"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="17 1 21 5 17 9" />
                            <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                            <polyline points="7 23 3 19 7 15" />
                            <path d="M21 13v2a4 4 0 0 1-4 4H3" />
                          </svg>
                          Поеми смяната
                        </button>
                      )}
                      {userHasPendingRequest && (
                        <span className="swap-pending-badge">Заявка изпратена</span>
                      )}
                      {/* Unassign button - only for current driver when not locked */}
                      {!isLocked && isUserDriver && (
                        <button
                          className="unassign-driver-btn"
                          onClick={() => handleUnassignDriver(firstRide.id)}
                          title="Отпиши се като шофьор"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="8.5" cy="7" r="4" />
                            <line x1="18" y1="8" x2="23" y2="13" />
                            <line x1="23" y1="8" x2="18" y2="13" />
                          </svg>
                          Отпиши ме
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="day-no-driver">
                      <div className="no-driver-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="8" x2="12" y2="12" />
                          <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                      </div>
                      <span>Нужен шофьор</span>
                      {!isLocked && !userHasPendingRequest && (
                        <button
                          className="volunteer-btn"
                          onClick={() => handleVolunteerDriver(firstRide.id)}
                        >
                          Записвам се
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Pending Swap Requests - visible to current driver */}
                {isUserDriver && pendingRequests.length > 0 && (
                  <div className="swap-requests-section">
                    <div className="swap-requests-header">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="17 1 21 5 17 9" />
                        <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                      </svg>
                      Заявки за смяна
                    </div>
                    {pendingRequests.map(request => (
                      <div key={request.id} className="swap-request-item">
                        <span className="swap-request-name">
                          {request.user.firstName} {request.user.lastName}
                        </span>
                        <div className="swap-request-actions">
                          <button
                            className="btn-success btn-sm"
                            onClick={() => handleApproveRequest(firstRide.id, request.id)}
                          >
                            Одобри
                          </button>
                          <button
                            className="btn-secondary btn-sm"
                            onClick={() => handleDeclineRequest(firstRide.id, request.id)}
                          >
                            Откажи
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Children List */}
                <div className="day-children-section">
                  <div className="day-children-header">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                    {dayRides.length} {dayRides.length === 1 ? 'дете' : 'деца'}
                  </div>
                  <div className="day-children-list">
                    {dayRides.map(ride => (
                      <div key={ride.id} className="day-child-item">
                        <div className="child-avatar-small">
                          {ride.child?.name?.charAt(0) || '?'}
                        </div>
                        <div className="child-details">
                          <span className="child-name">{ride.child?.name || 'Неизвестно'}</span>
                          {ride.pickupPoint?.address && (
                            <span className="child-location">{ride.pickupPoint.address}</span>
                          )}
                        </div>
                        {!isLocked && (
                          <button
                            onClick={() => handleDeleteRideClick(ride.id)}
                            className="btn-icon-xs"
                            title="Премахни"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* List View - compact table */
        <div className="schedule-list">
          <table className="rides-table">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Час</th>
                <th>Дете</th>
                <th>Шофьор</th>
                <th>Място</th>
                <th>Статус</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rides.map(ride => {
                const locked = isRideLocked(ride);
                return (
                  <tr key={ride.id} className={locked ? 'locked' : ''}>
                    <td>
                      {new Date(ride.scheduledDate).toLocaleDateString('bg-BG', {
                        day: 'numeric',
                        month: 'short'
                      })}
                    </td>
                    <td><strong>{ride.scheduledTime}</strong></td>
                    <td>{ride.child?.name}</td>
                    <td>
                      {ride.driver ? (
                        `${ride.driver.firstName} ${ride.driver.lastName}`
                      ) : (
                        <span className="text-warning">Няма</span>
                      )}
                    </td>
                    <td className="text-ellipsis">{ride.pickupPoint?.address || '-'}</td>
                    <td>
                      <span className={`status-badge-sm status-${ride.status}`}>
                        {ride.status}
                      </span>
                    </td>
                    <td>
                      {!locked && (
                        <button
                          onClick={() => handleDeleteRideClick(ride.id)}
                          className="btn-icon-sm btn-icon-danger"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                          </svg>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Ride Modal */}
      <Modal
        isOpen={showAddRideModal}
        onClose={() => setShowAddRideModal(false)}
        title="Добави пътуване"
        size="medium"
      >
        <form onSubmit={handleAddRide} className="add-ride-form">
          <div className="form-group">
            <label>Дата</label>
            <input
              type="date"
              value={newRide.scheduledDate}
              onChange={(e) => setNewRide({ ...newRide, scheduledDate: e.target.value })}
              required
              min={new Date().toISOString().split('T')[0]}
            />
          </div>

          <div className="form-group">
            <label>Час</label>
            <input
              type="time"
              value={newRide.scheduledTime}
              onChange={(e) => setNewRide({ ...newRide, scheduledTime: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Дете (по избор)</label>
            <select
              value={newRide.childId}
              onChange={(e) => setNewRide({ ...newRide, childId: e.target.value })}
            >
              <option value="">-- Избери дете --</option>
              {/* Children will be populated from carpool data */}
            </select>
          </div>

          <div className="form-group">
            <label>Шофьор (по избор)</label>
            <select
              value={newRide.driverId}
              onChange={(e) => setNewRide({ ...newRide, driverId: e.target.value })}
            >
              <option value="">-- Без шофьор --</option>
              {carpool?.members?.map(member => (
                <option key={member.user.id} value={member.user.id}>
                  {member.user.firstName} {member.user.lastName}
                </option>
              ))}
            </select>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={() => setShowAddRideModal(false)}>
              Отказ
            </button>
            <button type="submit" className="btn-primary">
              Добави
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Ride Modal */}
      <Modal
        isOpen={showDeleteRideModal}
        onClose={() => setShowDeleteRideModal(false)}
        title="Изтриване на пътуване"
        size="small"
      >
        <div className="confirm-modal-content">
          <div className="confirm-modal-icon danger">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </div>
          <p className="confirm-modal-description">
            Сигурни ли сте, че искате да изтриете това пътуване?
          </p>
          <div className="modal-footer">
            <button className="btn-secondary" onClick={() => setShowDeleteRideModal(false)}>
              Отказ
            </button>
            <button className="btn-danger" onClick={confirmDeleteRide}>
              Изтрий
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete All Modal */}
      <Modal
        isOpen={showDeleteAllModal}
        onClose={() => setShowDeleteAllModal(false)}
        title="Изтриване на целия график"
        size="small"
      >
        <div className="confirm-modal-content">
          <div className="confirm-modal-icon danger">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </div>
          <p className="confirm-modal-description">
            Сигурни ли сте, че искате да изтриете <strong>всички {rides.length} пътувания</strong>? Това действие е необратимо.
          </p>
          <div className="modal-footer">
            <button className="btn-secondary" onClick={() => setShowDeleteAllModal(false)}>
              Отказ
            </button>
            <button className="btn-danger" onClick={confirmDeleteSchedule}>
              Изтрий всички
            </button>
          </div>
        </div>
      </Modal>

      {/* Regenerate Modal */}
      <Modal
        isOpen={showRegenerateModal}
        onClose={() => setShowRegenerateModal(false)}
        title="Регенериране на график"
        size="small"
      >
        <div className="confirm-modal-content">
          <div className="confirm-modal-icon warning">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <p className="confirm-modal-description">
            Това ще изтрие съществуващия график и ще създаде нов. Сигурни ли сте?
          </p>
          <div className="modal-footer">
            <button className="btn-secondary" onClick={() => setShowRegenerateModal(false)}>
              Отказ
            </button>
            <button className="btn-primary" onClick={generateSchedule}>
              Регенерирай
            </button>
          </div>
        </div>
      </Modal>

      {/* Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Успех"
        size="small"
      >
        <div className="confirm-modal-content">
          <div className="confirm-modal-icon success">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <p className="confirm-modal-description">{successMessage}</p>
          <div className="modal-footer">
            <button className="btn-primary" onClick={() => setShowSuccessModal(false)}>
              ОК
            </button>
          </div>
        </div>
      </Modal>

      {/* Error Modal */}
      <Modal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Грешка"
        size="small"
      >
        <div className="confirm-modal-content">
          <div className="confirm-modal-icon danger">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <p className="confirm-modal-description">{errorMessage}</p>
          <div className="modal-footer">
            <button className="btn-primary" onClick={() => setShowErrorModal(false)}>
              ОК
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Schedule;
